import { useCallback, useEffect, useState } from 'react'

import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import FormControl from '@mui/material/FormControl'
import TextField from '@mui/material/TextField'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { GeblokkeerdeWekenWarning } from '@components/GeblokkeerdeWekenWarning'
import PlanningConflict from '@components/PlanningConflict'
import { useProgress } from '@hooks/useProgress'
import type { Entity } from '@models/Entity'
import type { GroupedOption } from '@models/GroupedOption'
import { formatDateToISOWeek, isDate } from '@util/date-util'
import { activiteittypes as hoofdleidingActiviteitTypes } from '@util/hoofdleiding-util'

import { ENTITIES } from '../../..'
import type { Project } from '../../../../aansluiting/project/types/Project'
import { SelectObject } from '../../../../core/components/SelectObject'
import { SelectString } from '../../../../core/components/SelectString'
import type { GeblokkeerdeWeek } from '../../../../planning/types/GeblokkeerdeWeek'
import { StyledError } from '../../../../theme/components/StyledError'
import { useCreateActiviteitenForEntity } from '../../hooks/useCreateActiviteitenForEntity'
import { usePatchActiviteitForEntity } from '../../hooks/usePatchActiviteitForEntity'
import type { Activiteit } from '../../types/Activiteit'

const activiteitTypes = [
  'Afspraak',
  'Vergunning aanvragen',
  'Bodemonderzoek aanvragen',
  'Hoofdleiding aanleggen',
  'Extra werkvoorbereiding',
  'Vestiging zakelijk recht',
  'Wacht op klant',
  'Klantcontact',
  'Netcongestie Elektra',
  'Voorcalculatie aanleveren',
]

const getActivityTypesByEntity = ({ type, projecttype }: Entity) => {
  if (type === ENTITIES.PROJECT && projecttype === 'Hoofdleiding')
    return hoofdleidingActiviteitTypes

  return activiteitTypes
}

const createOptionsForEdit = (
  assignees: GroupedOption[],
  assignedTo: string[],
) => {
  const toReturn: any[] = []

  assignees.forEach((x) => {
    if (assignedTo.includes(x.label)) toReturn.push(x)
  })

  return toReturn
}

type Props = {
  activiteit?: Activiteit
  isEdit: boolean
  assignees: GroupedOption[]
  entity: Entity
  entityProps?: Partial<Project>
  onClose: () => void
  geblokkeerdeWeken: GeblokkeerdeWeek[]
}

export const CreateEditActiviteitDialog = ({
  activiteit,
  isEdit,
  entity,
  assignees,
  entityProps,
  onClose,
  geblokkeerdeWeken,
}: Props) => {
  const progress = useProgress()

  const { mutate: createActiviteitenForEntity, error: createError } =
    useCreateActiviteitenForEntity()
  const { mutate: patchActiviteitForEntity, error: editError } =
    usePatchActiviteitForEntity()

  const error = isEdit ? editError : createError

  const [assignedTo, setAssignedTo] = useState<GroupedOption[]>([])
  const [text, setText] = useState('')
  const [errors, setErrors] = useState({})
  const [dueDate, setDueDate] = useState<Date | null>(null)
  const [type, setType] = useState('Afspraak')
  const activiteitLabel = `activiteit${assignedTo.length === 1 ? '' : 'en'}`

  const setValues = useCallback(
    (activiteit: Activiteit) => {
      const { assignedTo, text, type, dueDate } = activiteit

      setType(type)
      setAssignedTo(createOptionsForEdit(assignees, assignedTo))
      setText(text)
      setDueDate(dueDate ? new Date(dueDate) : null)
    },
    [assignees],
  )

  useEffect(() => {
    if (isEdit && activiteit) {
      setValues(activiteit)
    }
  }, [isEdit, activiteit, setValues])

  useEffect(() => {
    if (!isEdit) {
      setText('')
      setAssignedTo([])
      setDueDate(null)
      setErrors({})
    }
  }, [type, isEdit])

  const validate = () => {
    const textError = !text || text === ''
    const typeError = !type
    const assignedToError =
      type !== 'Afspraak' && type !== 'Klantcontact' && assignedTo.length === 0
    const dueDateError =
      !['Afspraak', 'Klantcontact', 'Netcongestie Elektra'].includes(type) &&
      !dueDate

    setErrors({
      textError,
      typeError,
      assignedToError,
      dueDateError,
    })

    return !textError && !typeError && !assignedToError && !dueDateError
  }

  const handleClickAnnuleren = () => {
    onClose()
    progress.clear()
  }

  const handleClickOpslaan = () => {
    if (!validate()) return

    if (isEdit && activiteit) {
      /** Editing is only for a single activiteit. */
      patchActiviteitForEntity(
        {
          entity,
          activiteitInfo: {
            _id: activiteit._id,
            text,
            dueDate: dueDate as (Date & string) | undefined,
          },
        },
        { onSuccess: () => onClose() },
      )
    } else if (
      /** Types Afspraak and Klantcontact don't need assignees. */
      (type === 'Afspraak' || type === 'Klantcontact') &&
      assignedTo.length === 0
    ) {
      createActiviteitenForEntity(
        {
          entity,
          progress: {
            ...progress,
            total: 1,
            current: 1,
            error: false,
          },
          activiteiten: [
            {
              type,
              entity: {
                id: entity.id,
                type: entity.type,
                gebiedscode: entity.gebiedscode,
              },
              assignedTo: [],
              text,
              dueDate: dueDate as (Date & string) | undefined,
            },
          ],
        },
        { onSuccess: () => onClose() },
      )
    } else {
      /** Creating activiteiten is done multiple at a time. */
      const activiteiten = assignedTo.map(({ label }) => ({
        type: type as Activiteit['type'],
        entity: {
          id: entity.id,
          type: entity.type,
          gebiedscode: entity.gebiedscode,
        },
        assignedTo: [label],
        text,
        dueDate: dueDate as (Date & string) | undefined,
      }))

      createActiviteitenForEntity(
        {
          entity,
          activiteiten,
          progress: {
            ...progress,
            total: activiteiten.length,
            current: 1,
            error: false,
          },
        },
        { onSuccess: () => onClose() },
      )
    }
  }

  return (
    <>
      <DialogTitle>
        {isEdit ? 'Bewerken activiteit' : 'Aanmaken activiteit'}
      </DialogTitle>

      <DialogContent>
        {type === 'Extra werkvoorbereiding' &&
          dueDate !== null &&
          !!entityProps &&
          !!entityProps.geplandeUitvoeringsdatum &&
          !!entityProps.geplandeUitvoeringsdatumType &&
          !!entityProps.combi && (
            <PlanningConflict
              geplandeUitvoeringsdatum={entityProps.geplandeUitvoeringsdatum}
              geplandeUitvoeringsdatumType={
                entityProps.geplandeUitvoeringsdatumType
              }
              opleverdatum={dueDate}
              combi={entityProps.combi}
            />
          )}

        <StyledError error={error} />

        <DialogContentText>
          {isEdit
            ? 'Bewerk een activiteit'
            : 'Kies een type activiteit om aan te maken en wijs hem optioneel toe aan een organisatie'}
        </DialogContentText>

        <GeblokkeerdeWekenWarning
          geblokkeerdeWeken={geblokkeerdeWeken}
          isoWeek={isDate(dueDate) && formatDateToISOWeek(dueDate)}
          gebiedscode={
            entity.gebiedscode ||
            /* v8 ignore next - TODO: missing coverage -- @preserve */ ''
          }
        />

        <form>
          <FormControl sx={{ mt: 1.5, width: '100%' }}>
            <SelectString
              options={getActivityTypesByEntity(entity)}
              onChange={(type) => setType(type)}
              disableClearable
              value={type}
              label="Type"
              error={errors['typeError']}
              disabled={isEdit}
            />
          </FormControl>

          <FormControl
            sx={{
              mt: 1.5,
              width: '100%',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <SelectObject
              multiple
              options={assignees}
              onChange={setAssignedTo}
              value={assignedTo}
              label="Toegewezen aan"
              error={errors['assignedToError']}
              sx={{ width: '50%', pr: 1, marginTop: 0.4 }}
              disabled={isEdit || type === 'Klantcontact'}
            />

            <DatePicker
              label="Opleverdatum"
              disablePast
              value={dueDate}
              onChange={setDueDate}
              displayWeekNumber
              disabled={type === 'Klantcontact'}
              slotProps={{
                field: { clearable: true },
                textField: {
                  variant: 'standard',
                  sx: { width: '50%' },
                  error: errors['dueDateError'],
                },
              }}
            />
          </FormControl>

          <FormControl sx={{ mt: 3, width: '100%' }}>
            <TextField
              value={text}
              multiline
              rows={6}
              label={`Opmerking (${text.length}/500)`}
              onChange={(e) => setText(e.target.value)}
              required
              error={errors['textError']}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
          </FormControl>
        </form>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClickAnnuleren} color="primary" autoFocus>
          Annuleren
        </Button>

        {isEdit && (
          <Button onClick={handleClickOpslaan} color="primary">
            Bewerken
          </Button>
        )}

        {!isEdit && progress.error && (
          <Button onClick={handleClickOpslaan} color="primary">
            Opnieuw proberen (vanaf {progress.current}e activiteit)
          </Button>
        )}

        {!isEdit && !progress.error && progress.total > progress.current && (
          <>
            <Button disabled>
              Aanmaken {activiteitLabel}... ({progress.current}/{progress.total}
              )
            </Button>
          </>
        )}

        {!isEdit && progress.current === 0 && (
          <Button onClick={handleClickOpslaan} color="primary">
            Aanmaken
            {assignedTo.length > 0 &&
              ` (${assignedTo.length} ${activiteitLabel})`}
          </Button>
        )}
      </DialogActions>
    </>
  )
}
